'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { IoArrowBackOutline, IoOpenOutline } from 'react-icons/io5';
import type { VideoCard } from './video-card-data';
import { getVideovizitkyCopy } from './copy';

export function VideoCards({
  cards,
  locale,
  introduction,
  descriptions,
}: {
  cards: VideoCard[];
  locale: string;
  introduction: ReactNode;
  descriptions: Record<string, ReactNode>;
}) {
  const copy = getVideovizitkyCopy(locale);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const detailHeading = useRef<HTMLHeadingElement>(null);
  const detailSection = useRef<HTMLElement>(null);
  const cardButtons = useRef(new Map<string, HTMLButtonElement>());
  const lastSelectedId = useRef<string | null>(null);
  const selectedCard = cards.find(card => card.id === selectedUser);

  useEffect(() => {
    const target = selectedUser
      ? detailHeading.current
      : lastSelectedId.current
        ? cardButtons.current.get(lastSelectedId.current)
        : null;
    target?.focus({ preventScroll: true });
    if (selectedUser) detailSection.current?.scrollIntoView({ block: 'start', behavior: 'auto' });
    else target?.scrollIntoView({ block: 'center', behavior: 'auto' });
  }, [selectedUser]);

  if (selectedCard) {
    return (
      <article
        ref={detailSection}
        className="flex w-full min-w-0 scroll-mt-32 flex-col gap-8 sm:gap-12 lg:scroll-mt-56"
      >
        <button
          type="button"
          onClick={() => {
            setSelectedUser(null);
          }}
          className="inline-flex min-h-11 items-center gap-2 self-start rounded-lg px-2 font-semibold text-primary hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
        >
          <IoArrowBackOutline size={20} aria-hidden="true" />
          {copy.back}
        </button>
        <h1
          ref={detailHeading}
          tabIndex={-1}
          className="break-words text-center text-3xl font-bold leading-tight text-foreground outline-none sm:text-4xl lg:text-5xl"
        >
          {selectedCard.name}
        </h1>
        <div className="mx-auto grid w-full max-w-5xl items-start gap-8 md:grid-cols-2 md:gap-12">
          {descriptions[selectedCard.id]}
          <div
            className={`relative order-first aspect-video min-w-0 overflow-hidden rounded-2xl bg-white md:order-none ${selectedCard.description ? '' : 'md:col-span-2 md:mx-auto md:w-full md:max-w-xl'}`}
          >
            <Image
              src={selectedCard.thumbnailUrl}
              alt={selectedCard.thumbnailAlt}
              fill
              sizes="(max-width: 767px) 100vw, 512px"
              className="object-contain"
            />
          </div>
        </div>
        <section className="mx-auto flex w-full max-w-5xl flex-col gap-5" aria-label={copy.video}>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            <h2 className="text-center text-2xl font-bold text-foreground">{copy.video}</h2>
            <a
              href={selectedCard.videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${copy.watch} (${copy.newTab})`}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-lg px-1 text-sm font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
            >
              YouTube
              <IoOpenOutline size={18} aria-hidden="true" />
            </a>
          </div>
          <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-black">
            <iframe
              src={`${selectedCard.videoEmbedUrl}&autoplay=0`}
              title={`${copy.video}: ${selectedCard.name}`}
              className="absolute inset-0 h-full w-full border-0"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </section>
      </article>
    );
  }

  return (
    <>
      {introduction}
      <ul className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
        {cards.map(card => (
          <li key={card.id} className="min-w-0">
            <button
              type="button"
              ref={element => {
                if (element) cardButtons.current.set(card.id, element);
                else cardButtons.current.delete(card.id);
              }}
              onClick={() => {
                lastSelectedId.current = card.id;
                setSelectedUser(card.id);
              }}
              aria-label={`${copy.showDetail}: ${card.name}`}
              className="group flex h-full w-full flex-col overflow-hidden rounded-2xl border border-border bg-white text-left hover:border-primary/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
            >
              <span className="relative block aspect-video w-full overflow-hidden bg-primary/10">
                <Image
                  src={card.thumbnailUrl}
                  alt={card.thumbnailAlt}
                  fill
                  sizes="(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 33vw"
                  className="object-cover motion-safe:transition-transform motion-safe:duration-300 motion-safe:group-hover:scale-105"
                />
              </span>
              <span className="w-full break-words p-6 text-center text-xl font-bold text-foreground lg:p-7">
                {card.name}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}
